<template>
  <Slidein data-cy="side-menu" :extra-classes="['slidein-menu']" type="menu">
    <template v-slot:content>
      <div class="slidein__header">
        <a
          class="slidein__close"
          data-cy="side-menu-close"
          @click="
            appStore.showSlidein('menu', false)
          "
          :title="t('global.back')"
        >
          <Arrow />
        </a>
        <router-link
          to="/"
          class="slidein__logo"
          @click.native="
            appStore.showSlidein('menu', false)
          "
        >
          <img
            class="slidein__logo-img"
            :src="supplierData.logoUrl"
            :alt="supplierData.name"
          />
        </router-link>
      </div>

      <div class="slidein__content">
        <h2 v-if="availableCubes.length > 1" class="slidein__headline">
          Meine Cubes
        </h2>
        <template v-if="!isDemoUser">
          <ul v-if="availableCubes.length > 1" class="slidein__list">
            <li
              v-for="cube in availableCubes"
              :key="cube.cubeName"
              :class="{
                'slidein__list-item': true,
              }"
            >
              <a
                :class="{
                  slidein__link: true,
                  'slidein__link--selected-cube': cube.isSelected,
                }"
                @click="cube.callback(cube)"
              >
                {{ cube.nickName }}
                <Check v-if="cube.isSelected" />
              </a>
            </li>
          </ul>
          <div v-else class="slidein__cube">
            <img class="slidein__cube-icon" :src="iconUrlSvg" v-if="iconUrlSvg" />
            <span class="slidein__cube-nickname">{{ nickName }}</span>
          </div>
        </template>

        <ul class="slidein__list">
          <li
            v-for="item in menuItems"
            :key="item.title"
            class="slidein__list-item"
          >
            <router-link
              :to="item.link"
              class="slidein__link"
              :data-cy="item.cypressId"
              @click.native="menuLinkClicked(item, $event)"
            >
              {{ t(item.text) }}
              <span
                class="slidein__link-notifications"
                v-if="getNumNotifications(item.notificationTypeGroup) > 0"
              >
                {{ getNumNotifications(item.notificationTypeGroup) }}
              </span>
              <component v-else-if="item.icon" :is="item.icon" />
            </router-link>
          </li>
        </ul>
      </div>
    </template>
  </Slidein>
</template>

<script>
  import Slidein from '@/components/slidein/Slidein.vue'
  import Arrow from '@/components/icons/Arrow.vue'
  import Check from '@/components/icons/Check.vue'
  import Edit from '@/components/icons/Edit.vue'
  import Caret from '@/components/icons/Caret.vue'
  import { useAppStore } from '@/stores/useAppStore'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed } from 'vue'
  import { useCubeImages } from '@/composables/useCubeImages.ts'
  import { useRouter } from 'vue-router'
  import { useAccountStore } from '@/stores/useAccountStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { storeToRefs } from 'pinia'

  const MENU_ITEMS = [
    {
      link: '/choose-provider/home',
      text: 'becomeEfriend.menuTitle',
      icon: 'Caret',
      visible: ['isDemoUser']
    },
    {
      link: '/notifications',
      text: 'notifications.menuTitle',
      icon: 'Caret',
      notificationTypeGroup: 'any'
    },
    {
      link: '/my-data',
      text: 'myData.menuTitle',
      icon: 'Caret',
      visible: ['!isDemoUser']
    },
    {
      link: '/settings',
      text: 'settings.menuTitle',
      icon: 'Caret',
      visible: ['!isDemoUser']
    },
    {
      link: '/invoices',
      text: 'invoices.menuTitle',
      icon: 'Caret',
      notificationTypeGroup: 'invoices',
      visible: ['!isDemoUser']
    },
    {
      link: '/downloads',
      text: 'downloads.menuTitle',
      icon: 'Caret',
      visible: ['!isDemoUser']
    },
    {
      link: '/experts',
      text: 'experts.menuTitle',
      icon: 'Caret',
      visible: ['!isDemoUser', '!isCommunityless']
    },
    {
      link: '/api-manager',
      text: 'apiManager.menuTitle',
      icon: 'Caret',
      visible: ['!isDemoUser', '!isCommunityless']
    },
    {
      link: '/about-app',
      text: 'aboutApp.menuTitle',
      icon: 'Caret'
    },
    {
      link: '/help',
      text: 'help.menuTitle',
      icon: 'Caret'
    },
    {
      link: '/tour',
      text: 'tour.menuTitle',
      icon: 'Caret'
      // click: 'resetTour'
    },
    {
      link: '/login',
      text: 'global.logout',
      cypressId: 'logout'
    }
  ]

export default {
  name: 'slidein-menu',
  components: {
    Edit,
    Slidein,
    Arrow,
    Check,
    Caret,
  },
  setup() {
    const router = useRouter()
    const appStore = useAppStore()
    const { t } = useStrings()
    const cubeStore = useCubeStore()
    const { iconUrlSvg } = useCubeImages(storeToRefs(cubeStore).name)
    const accountStore = useAccountStore()
    const { checkAccountProperties } = accountStore
    const { isDemoUser } = storeToRefs(accountStore)

    const menuItems = computed(() => {
      return MENU_ITEMS.filter((item) => {
          if (!('visible' in item)) {
            return true
          }
          return checkAccountProperties(item.visible)
        })
    })
    const availableCubes = computed(() => {
      return accountStore.availableCubes.map((cube) => ({
        ...cube,
        isSelected: cube.cubeName === String(cubeStore.name),
        callback: (c) => {
          appStore.selectCube({ cube: c.cubeName, currentRoute: router.currentRoute.value })
        },
      }))
    })

    const menuLinkClicked = (menuItem, ev) => {
      appStore.showSlidein('menu', false)
      if (typeof menuItem.callback === 'function') {
        menuItem.callback(menuItem, ev)
      }
    }

    return {
      menuItems,
      appStore,
      supplierData: computed(() => cubeStore.supplier),
      isDemoUser,
      nickName: computed(() => cubeStore.nickName),
      getNumNotifications: (type) => {
        return cubeStore.numNotificationsForTypeGroup(type)
      },
      availableCubes,
      menuLinkClicked,
      t,
      iconUrlSvg,
    }
  },
}
</script>

<style lang="scss">
.slidein__cube-edit {
  color: $grey-dark;

  .icon {
    width: 1em;
  }
}
.slidein__cube-icon {
  height: 100px;
}
</style>
